if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      threeColumnSource = undefined;
      threeColumnMiddle = undefined;
      threeColumnRight = undefined;
      threeColumnBlocks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.product-form__quantity .quantity__input');
        this.syncThreeColumnLayout = this.syncThreeColumnLayout.bind(this);
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initThreeColumnLayout();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      initThreeColumnLayout() {
        const threeColumnContainer = this.querySelector('.product__info-container--three-column');
        if (!threeColumnContainer) return;

        this.threeColumnSource = threeColumnContainer.querySelector('.product__info-source');
        this.threeColumnMiddle = threeColumnContainer.querySelector('.product__info-columns--middle');
        this.threeColumnRight = threeColumnContainer.querySelector('.product__info-columns--right');

        if (!this.threeColumnSource || !this.threeColumnMiddle || !this.threeColumnRight) return;

        this.threeColumnBlocks = Array.from(this.threeColumnSource.children);
        this.syncThreeColumnLayout();
      }

      syncThreeColumnLayout() {
        if (!this.threeColumnSource || !this.threeColumnMiddle || !this.threeColumnRight) return;

        const isDesktop = window.matchMedia('(min-width: 990px)').matches;

        if (isDesktop) {
          this.threeColumnBlocks.forEach((block) => {
            if (block.classList.contains('product__info-block--right')) {
              this.threeColumnRight.appendChild(block);
            } else {
              this.threeColumnMiddle.appendChild(block);
            }
          });
          this.classList.add('product-info--three-column-ready');
        } else {
          this.threeColumnBlocks.forEach((block) => {
            this.threeColumnSource.appendChild(block);
          });
          this.classList.remove('product-info--three-column-ready');
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            const optionInput = document.getElementById(targetId);
            const optionControl = targetId
              ? document.querySelector(`[data-variant-input="${CSS.escape(targetId)}"]`)
              : null;
            (optionControl || optionInput)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          const variantAnnouncement = this.querySelector('[data-variant-announcement]');
          if (variantAnnouncement && variant?.title) {
            variantAnnouncement.textContent = `${variant.title} selected`;
          }

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('title');
          updateSourceFromDestination('price');
          updateSourceFromDestination('Afterpay');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        this.quantityInput =
          this.querySelector('.product-form__quantity .quantity__input') || this.querySelector('.quantity__input');
        if (!this.quantityInput) return;

        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max !== null) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'data-available-quantity', 'max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('pdp-countdown-timer')) {
  customElements.define(
    'pdp-countdown-timer',
    class PdpCountdownTimer extends HTMLElement {
      connectedCallback() {
        this.messageEl = this.querySelector('[data-pdp-timer-message]');
        this.valueEls = {
          days: this.querySelector('[data-timer-value="days"]'),
          hours: this.querySelector('[data-timer-value="hours"]'),
          minutes: this.querySelector('[data-timer-value="minutes"]'),
          seconds: this.querySelector('[data-timer-value="seconds"]'),
        };

        this.tick = this.tick.bind(this);
        this.tick();
        this.intervalId = window.setInterval(this.tick, 1000);
      }

      disconnectedCallback() {
        if (this.intervalId) {
          window.clearInterval(this.intervalId);
          this.intervalId = null;
        }
      }

      parseTimerDate(value, isEndDate = false) {
        const rawValue = String(value || '').trim();
        if (!rawValue) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
          return new Date(`${rawValue}T${isEndDate ? '23:59:59' : '00:00:00'}`);
        }

        if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(rawValue)) {
          return new Date(rawValue.replace(' ', 'T'));
        }

        return new Date(rawValue);
      }

      formatUnit(value) {
        return String(Math.max(0, Math.floor(value))).padStart(2, '0');
      }

      updateMessage(text) {
        if (this.messageEl) {
          this.messageEl.textContent = text;
        }
      }

      updateValues(remainingMs) {
        const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (this.valueEls.days) this.valueEls.days.textContent = this.formatUnit(days);
        if (this.valueEls.hours) this.valueEls.hours.textContent = this.formatUnit(hours);
        if (this.valueEls.minutes) this.valueEls.minutes.textContent = this.formatUnit(minutes);
        if (this.valueEls.seconds) this.valueEls.seconds.textContent = this.formatUnit(seconds);
      }

      tick() {
        const startDate = this.parseTimerDate(this.dataset.startDatetime);
        const endDate = this.parseTimerDate(this.dataset.endDatetime, true);
        const upcomingLabel = this.dataset.upcomingLabel || 'Starts in';
        const liveLabel = this.dataset.liveLabel || 'Ends in';

        if (!endDate || Number.isNaN(endDate.getTime())) {
          this.hidden = true;
          return;
        }

        if (startDate && Number.isNaN(startDate.getTime())) {
          this.hidden = true;
          return;
        }

        const now = new Date();

        if (startDate && now < startDate) {
          this.hidden = true;
          this.updateMessage(upcomingLabel);
          this.updateValues(startDate.getTime() - now.getTime());
          return;
        }

        if (now >= endDate) {
          this.hidden = true;
          return;
        }

        this.hidden = false;
        this.updateMessage(liveLabel);
        this.updateValues(endDate.getTime() - now.getTime());
      }
    }
  );
}

(function () {
  const WIDGET_SELECTOR = '[data-volume-discount-root]';
  const QUANTITY_SELECTOR = [
    'product-info .product-form__quantity .quantity__input',
    'product-info input[name="quantity"]',
    '#sticky-atc-quantity',
    '.sticky_atc_wrapper input[name="quantity"]',
  ].join(', ');

  const widgetState = new WeakMap();
  let renderTimer = null;
  let listenersBound = false;
  let syncingQuantity = false;

  function scheduleRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(renderAllWidgets, 0);
  }

  function parseJSON(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  function normalizeCountryCode(value) {
    const code = String(value || '').trim().toUpperCase();
    return code || 'DEFAULT';
  }

  function normalizeTier(rawTier, index) {
    if (!rawTier || typeof rawTier !== 'object') return null;

    const discountType = String(rawTier.discount_type || rawTier.discountType || '').toLowerCase();
    const discountValue = Number(rawTier.discount_value ?? rawTier.discountValue);
    const minQuantity = Number(rawTier.min_quantity ?? rawTier.minQty);
    const rawMaxQuantity = rawTier.max_quantity ?? rawTier.maxQty;
    const maxQuantity =
      rawMaxQuantity === null || rawMaxQuantity === '' || rawMaxQuantity === undefined
        ? null
        : Number(rawMaxQuantity);

    if (discountType && discountType !== 'percentage') return null;
    if (!Number.isFinite(minQuantity) || minQuantity < 1) return null;
    if (!Number.isFinite(discountValue) || discountValue <= 0) return null;
    if (maxQuantity !== null && (!Number.isFinite(maxQuantity) || maxQuantity < minQuantity)) return null;

    return {
      index: String(index),
      min: Math.floor(minQuantity),
      max: maxQuantity === null ? null : Math.floor(maxQuantity),
      discount: discountValue,
      key: [
        Math.floor(minQuantity),
        maxQuantity === null ? '' : Math.floor(maxQuantity),
        discountType || 'percentage',
        discountValue,
      ].join(':'),
    };
  }

  function normalizeTiers(rawTiers) {
    if (!Array.isArray(rawTiers)) return [];

    return rawTiers
      .map((tier, index) => normalizeTier(tier, index))
      .filter(Boolean)
      .sort((a, b) => a.min - b.min || (a.max || Infinity) - (b.max || Infinity));
  }

  function resolveTierSetFromScope(scope, countryCode) {
    if (!scope || typeof scope !== 'object') return [];

    const tiersByCountry =
      scope.tiers_by_country && typeof scope.tiers_by_country === 'object' ? scope.tiers_by_country : null;

    if (tiersByCountry) {
      if (Object.prototype.hasOwnProperty.call(tiersByCountry, countryCode)) {
        return normalizeTiers(tiersByCountry[countryCode]);
      }

      if (Object.prototype.hasOwnProperty.call(tiersByCountry, 'default')) {
        return normalizeTiers(tiersByCountry.default);
      }
    }

    return normalizeTiers(scope.tiers);
  }

  function hasVariantSpecificConfig(entry) {
    if (!entry || typeof entry !== 'object') return false;
    return Boolean(
      (entry.override && entry.override.is_overridden) ||
        Array.isArray(entry.tiers) ||
        (entry.tiers_by_country && typeof entry.tiers_by_country === 'object')
    );
  }

  function getWidgetState(widget) {
    if (widgetState.has(widget)) {
      return widgetState.get(widget);
    }

    const configScript = widget.querySelector('[data-volume-discount-config]');
    const parsedConfig = configScript ? parseJSON(configScript.textContent || '') : null;
    const state = { config: parsedConfig };

    widgetState.set(widget, state);
    return state;
  }

  function syncWidgetVariantData(widget, variant) {
    if (!widget || !variant) return;

    const basePrice = Number(variant.price || 0);
    const compareAtPrice = Number(variant.compare_at_price || 0);

    widget.dataset.volumeDiscountCurrentVariant = String(variant.id || '');
    widget.dataset.volumeDiscountInventory = String(variant.inventory_quantity ?? '');
    widget.dataset.volumeDiscountBasePrice = String(basePrice);
    widget.dataset.volumeDiscountBaseCompare = String(compareAtPrice > basePrice ? compareAtPrice : basePrice);
  }

  function getProductInfoBySectionId(sectionId) {
    if (!sectionId) return null;
    return document.querySelector(`product-info[data-section="${CSS.escape(String(sectionId))}"]`);
  }

  function getSelectedVariantFromProductInfo(productInfo) {
    if (!productInfo) return null;

    const selectedVariantScript = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!selectedVariantScript) return null;

    return parseJSON(selectedVariantScript.textContent || '');
  }

  function syncWidgetsForVariant(variant, sectionId) {
    if (!variant) return;

    const widgets = [];
    const productInfo = getProductInfoBySectionId(sectionId);

    if (productInfo) {
      widgets.push(...productInfo.querySelectorAll(WIDGET_SELECTOR));
    }

    const stickyWidget = document.querySelector('#sticky-atc-wrapper [data-volume-discount-root]');
    if (stickyWidget) {
      const productId = productInfo?.dataset.productId || '';
      if (!productId || stickyWidget.dataset.volumeDiscountProduct === productId) {
        widgets.push(stickyWidget);
      }
    }

    widgets.forEach((widget) => syncWidgetVariantData(widget, variant));
  }

  function getPriceContainer(widget) {
    const directContainer = widget.closest('[data-volume-discount-price-container]');
    if (directContainer) return directContainer;

    const productId = widget.dataset.volumeDiscountProduct;
    const productInfo = widget.closest('product-info');
    if (productInfo) {
      const scopedContainer = productInfo.querySelector('[data-volume-discount-price-container]');
      if (scopedContainer) return scopedContainer;
    }

    const stickyWrapper = widget.closest('#sticky-atc-wrapper, .sticky_atc_wrapper');
    if (stickyWrapper) {
      const stickyContainer = stickyWrapper.querySelector('[data-volume-discount-price-container]');
      if (stickyContainer) return stickyContainer;
    }

    if (productId) {
      const matchedContainer = document.querySelector(
        `[data-volume-discount-root][data-volume-discount-product="${CSS.escape(productId)}"]`
      )?.closest('product-info')?.querySelector('[data-volume-discount-price-container]');

      if (matchedContainer) return matchedContainer;
    }

    return widget.parentElement;
  }

  function getQuantityInput(widget) {
    const localContainer =
      widget.closest('product-info') ||
      widget.closest('#sticky-atc-wrapper') ||
      widget.closest('.sticky_atc_wrapper') ||
      document;

    return (
      localContainer.querySelector('.product-form__quantity .quantity__input') ||
      localContainer.querySelector('.product-form__quantity input[name="quantity"]') ||
      localContainer.querySelector('#sticky-atc-quantity') ||
      localContainer.querySelector('input[name="quantity"]') ||
      document.querySelector('product-info .product-form__quantity .quantity__input') ||
      document.querySelector('#sticky-atc-quantity') ||
      document.querySelector('input[name="quantity"]')
    );
  }

  function getQuantity(widget) {
    const input = getQuantityInput(widget);
    const min = input ? Number(input.min || 1) : 1;
    const value = input ? Number(input.value || min) : 1;
    return Math.max(min, Number.isFinite(value) ? value : min);
  }

  function getMaxQuantity(widget) {
    const input = getQuantityInput(widget);
    const inputMax = input && input.max !== '' ? Number(input.max) : Infinity;
    const shopMax = Number(widget?.dataset.volumeDiscountShopLimit);
    const inventoryQuantity = Number(widget?.dataset.volumeDiscountInventory);
    const maxValues = [inputMax, shopMax, inventoryQuantity].filter(Number.isFinite);
    const maxQuantity = maxValues.length ? Math.min(...maxValues) : Infinity;

    return Number.isFinite(maxQuantity) ? maxQuantity : Infinity;
  }

  function setQuantityInputs(qty) {
    if (syncingQuantity) return;

    syncingQuantity = true;
    const normalizedQty = Math.max(1, Number(qty) || 1);

    document.querySelectorAll(QUANTITY_SELECTOR).forEach((input) => {
      const max = input.max === '' ? Infinity : Number(input.max);
      input.value = Number.isFinite(max) ? Math.min(normalizedQty, max) : normalizedQty;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    syncingQuantity = false;
    scheduleRender();
  }

  function getCurrentVariantId(widget) {
    const directValue = widget.dataset.volumeDiscountCurrentVariant;
    if (directValue) return String(directValue);

    const productInfo = widget.closest('product-info');
    const stickyVariantSelect = widget.closest('#sticky-atc-wrapper')?.querySelector('#sticky-atc-variant');
    const hiddenVariantInput = productInfo?.querySelector('input[name="id"]');

    if (stickyVariantSelect && stickyVariantSelect.value) {
      return String(stickyVariantSelect.value);
    }

    if (hiddenVariantInput && hiddenVariantInput.value) {
      return String(hiddenVariantInput.value);
    }

    return '';
  }

  function getMoneyFormat(widget) {
    const useCurrency = widget.dataset.volumeDiscountCurrencyCodeEnabled === 'true';
    const withCurrency = widget.dataset.volumeDiscountMoneyWithCurrencyFormat || widget.dataset.volumeDiscountMoneyFormat;
    const plain = widget.dataset.volumeDiscountMoneyFormat || withCurrency;
    return useCurrency ? withCurrency : plain;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (Number.isNaN(number) || number === null) return '0';

    const parts = (number / 100).toFixed(precision).split('.');
    const integerPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return integerPart + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, widget) {
    const format = getMoneyFormat(widget) || '${{amount}}';
    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

    let formatted = '0';
    switch (placeholder) {
      case 'amount':
      case 'amount_with_currency':
        formatted = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formatted = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  function getRoundedDiscountPercent(comparePrice, finalPrice) {
    if (!comparePrice || comparePrice <= finalPrice) return 0;

    // Round to the nearest whole percent so the badge matches the displayed tier math.
    return Math.round(((comparePrice - finalPrice) * 100) / comparePrice);
  }

  function findTierByQuantity(quantity, tiers) {
    let matched = null;
    (tiers || []).forEach((tier) => {
      if (quantity >= tier.min && (tier.max === null || quantity <= tier.max)) {
        matched = tier;
      }
    });
    return matched;
  }

  function getBaseState(widget) {
    const basePrice = Number(widget?.dataset.volumeDiscountBasePrice || 0);
    const compare = Number(widget?.dataset.volumeDiscountBaseCompare || basePrice);
    return { price: basePrice, compare };
  }

  function calculateTierPricing(widget, tier) {
    const { price: basePrice, compare: baseCompare } = getBaseState(widget);
    const saleActive = baseCompare > basePrice;
    const baseDiscountPercent = saleActive ? getRoundedDiscountPercent(baseCompare, basePrice) : 0;
    const tierDiscount = tier ? Number(tier.discount || 0) : 0;
    const combinedDiscountPercent = tier ? Math.min(100, baseDiscountPercent + tierDiscount) : baseDiscountPercent;
    const pricingBase = saleActive ? baseCompare : basePrice;
    const finalPrice = tier ? Math.round((pricingBase * (100 - combinedDiscountPercent)) / 100) : basePrice;
    const comparePrice = baseCompare > finalPrice ? baseCompare : basePrice;
    const savings = Math.max(0, comparePrice - finalPrice);

    return {
      basePrice,
      baseCompare,
      baseDiscountPercent,
      comparePrice,
      combinedDiscountPercent,
      finalPrice,
      displayDiscountPercent: combinedDiscountPercent,
      pricingBase,
      saleActive,
      savings,
      tierDiscount,
    };
  }

  function getResolvedTiers(widget) {
    const state = getWidgetState(widget);
    const config = state.config;
    if (!config) return [];

    const countryCode = normalizeCountryCode(widget.dataset.volumeDiscountCountry);
    const variantId = getCurrentVariantId(widget);
    const variants = config.variants && typeof config.variants === 'object' ? config.variants : {};
    const variantEntry = variantId ? variants[variantId] : null;

    if (hasVariantSpecificConfig(variantEntry)) {
      return resolveTierSetFromScope(variantEntry, countryCode);
    }

    return resolveTierSetFromScope(config, countryCode);
  }

  function updatePriceContainer(widget, finalPrice, comparePrice, discountPercent) {
    const container = getPriceContainer(widget);
    if (!container) return;

    const price = container.querySelector('.price');
    if (!price) return;

    const isOnSale = comparePrice > finalPrice;
    const finalMoney = formatMoney(finalPrice, widget);
    const compareMoney = formatMoney(comparePrice, widget);

    price.classList.toggle('price--on-sale', isOnSale);

    const salePriceNode = price.querySelector('.price__sale .price-item--sale');
    const comparePriceNode = price.querySelector('.price__sale .price-item--regular');
    const regularPriceNode = price.querySelector('.price__regular .price-item--regular');
    const discountNode = price.querySelector('[data-price-discount]');

    if (salePriceNode) salePriceNode.innerHTML = finalMoney;
    if (comparePriceNode) comparePriceNode.innerHTML = compareMoney;
    if (regularPriceNode) regularPriceNode.innerHTML = isOnSale ? compareMoney : finalMoney;

    if (discountNode) {
      if (isOnSale) {
        const safeDiscountPercent = Number.isFinite(discountPercent) ? Math.max(0, Math.round(discountPercent)) : 0;
        const sr = discountNode.querySelector('[data-price-discount-sr]');
        const visible = discountNode.querySelector('[data-price-discount-visible]');
        if (sr && visible) {
          sr.textContent = `${safeDiscountPercent} percent off`;
          visible.innerHTML = `-${safeDiscountPercent}% <span class="discount-off-text">OFF</span>`;
        } else {
          discountNode.innerHTML = `-${safeDiscountPercent}% <span class="discount-off-text">OFF</span>`;
        }
        discountNode.hidden = false;
      } else {
        discountNode.textContent = '';
        discountNode.hidden = true;
      }
    }
  }
    const VOLUME_DISCOUNT_CELEBRATION_MS = 6000;
  const VOLUME_DISCOUNT_CONFETTI = [
    { x: '-46px', y: '-22px', r: '-36deg', d: '0ms', c: '#7ac943' },
    { x: '-32px', y: '-48px', r: '16deg', d: '50ms', c: '#8b5cf6', round: true },
    { x: '-14px', y: '-66px', r: '-14deg', d: '20ms', c: '#f2aa00' },
    { x: '4px', y: '-72px', r: '24deg', d: '80ms', c: '#3b82f6', round: true },
    { x: '22px', y: '-58px', r: '-22deg', d: '30ms', c: '#22c55e' },
    { x: '40px', y: '-34px', r: '34deg', d: '60ms', c: '#f59e0b' },
    { x: '-52px', y: '-8px', r: '46deg', d: '40ms', c: '#a78bfa' },
    { x: '48px', y: '-10px', r: '-42deg', d: '70ms', c: '#34d399', round: true },
    { x: '-22px', y: '-36px', r: '10deg', d: '15ms', c: '#f97316' },
    { x: '14px', y: '-42px', r: '-30deg', d: '55ms', c: '#60a5fa' },
    { x: '30px', y: '-18px', r: '12deg', d: '35ms', c: '#eab308', round: true },
    { x: '-8px', y: '-24px', r: '-8deg', d: '25ms', c: '#4ade80' },
  ];
  const VOLUME_DISCOUNT_CELEBRATION_ICON = `<svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
<g clip-path="url(#clip0_1186_377)">
<path d="M10.7611 11.8372C10.5601 11.6488 3.56847 5.09411 3.37366 4.9115C3.16969 4.72025 2.83894 4.72025 2.63494 4.9115C2.43094 5.10275 2.43094 5.4128 2.63494 5.60405L3.14097 6.07846L0.0306245 14.3466C-0.114969 14.7336 0.283625 15.1079 0.696999 14.9713L9.51637 12.0553L10.0224 12.5297C10.2264 12.721 10.5571 12.721 10.7611 12.5297C10.9651 12.3385 10.9651 12.0284 10.7611 11.8372ZM1.37891 13.7073L2.03387 11.9662L3.23603 13.0932L1.37891 13.7073ZM4.32828 12.7321L2.41906 10.9422L2.99691 9.40623L5.96669 12.1904L4.32828 12.7321ZM7.05894 11.8293L3.38209 8.38222L3.95994 6.84621L8.69734 11.2876L7.05894 11.8293Z" fill="#188A06"/>
<path d="M12.0218 3.72913C11.8178 3.53788 11.4871 3.53788 11.2831 3.72913L8.02214 6.78626C7.81814 6.97751 7.81814 7.28756 8.02214 7.47881C8.22611 7.67006 8.55686 7.67009 8.76086 7.47881L12.0218 4.42168C12.2258 4.23043 12.2258 3.92038 12.0218 3.72913Z" fill="#188A06"/>
<path d="M11.4789 8.23096H9.65323C9.36473 8.23096 9.13086 8.45021 9.13086 8.72068C9.13086 8.99115 9.36473 9.21041 9.65323 9.21041H11.4789C11.7674 9.21041 12.0013 8.99115 12.0013 8.72068C12.0013 8.45021 11.7674 8.23096 11.4789 8.23096Z" fill="#188A06"/>
<path d="M6.69816 3.74902C6.40966 3.74902 6.17578 3.96825 6.17578 4.23875V5.95033C6.17578 6.2208 6.40966 6.44006 6.69816 6.44006C6.98666 6.44006 7.22053 6.2208 7.22053 5.95033V4.23875C7.22053 3.96831 6.98666 3.74902 6.69816 3.74902Z" fill="#188A06"/>
<path d="M15.5417 7.68165C14.9292 7.10743 13.938 7.1074 13.3254 7.68165C12.7144 8.25446 12.7144 9.18651 13.3254 9.75933C13.9378 10.3335 14.9291 10.3336 15.5416 9.75933C16.1527 9.18651 16.1527 8.25449 15.5417 7.68165ZM14.8029 9.06678C14.5988 9.25815 14.2684 9.2582 14.0642 9.06678C13.8606 8.87582 13.8606 8.56516 14.0642 8.37423C14.2684 8.1828 14.5988 8.1828 14.8029 8.37423C15.0071 8.56565 15.0071 8.87535 14.8029 9.06678Z" fill="#188A06"/>
<path d="M7.8064 0.42962C7.1954 -0.143192 6.20121 -0.143221 5.59021 0.42962C4.9778 1.00375 4.97768 1.93308 5.59021 2.50733C6.20112 3.08011 7.19537 3.08017 7.8064 2.5073C8.41884 1.93314 8.41893 1.00384 7.8064 0.42962ZM7.06762 1.81475C6.86396 2.00567 6.53259 2.00567 6.3289 1.81475C6.1248 1.62335 6.12477 1.31362 6.3289 1.1222C6.53312 0.930773 6.86346 0.930773 7.06762 1.1222C7.2718 1.31359 7.2718 1.62329 7.06762 1.81475Z" fill="#188A06"/>
<path d="M13.3466 6.44015C13.6351 6.44015 13.869 6.22089 13.869 5.95042C13.869 5.67995 13.6351 5.46069 13.3466 5.46069C13.0581 5.46069 12.8242 5.67995 12.8242 5.95042C12.8242 6.22089 13.0581 6.44015 13.3466 6.44015Z" fill="#188A06"/>
<path d="M9.65323 2.97726C9.94173 2.97726 10.1756 2.758 10.1756 2.48753C10.1756 2.21706 9.94173 1.9978 9.65323 1.9978C9.36473 1.9978 9.13086 2.21706 9.13086 2.48753C9.13086 2.758 9.36473 2.97726 9.65323 2.97726Z" fill="#188A06"/>
<path d="M13.3466 11.9804C13.6351 11.9804 13.869 11.7612 13.869 11.4907C13.869 11.2202 13.6351 11.001 13.3466 11.001C13.0581 11.001 12.8242 11.2202 12.8242 11.4907C12.8242 11.7612 13.0581 11.9804 13.3466 11.9804Z" fill="#188A06"/>
<path d="M3.74308 2.97726C4.03158 2.97726 4.26545 2.758 4.26545 2.48753C4.26545 2.21706 4.03158 1.9978 3.74308 1.9978C3.45458 1.9978 3.2207 2.21706 3.2207 2.48753C3.2207 2.758 3.45458 2.97726 3.74308 2.97726Z" fill="#188A06"/>
<path d="M13.8681 0.0388184C13.5796 0.0388184 13.3457 0.258076 13.3457 0.528545V1.01824C13.3457 1.28871 13.5796 1.50797 13.8681 1.50797C14.1566 1.50797 14.3905 1.28871 14.3905 1.01824V0.528516C14.3905 0.258076 14.1565 0.0388184 13.8681 0.0388184Z" fill="#188A06"/>
<path d="M13.8681 2.48755C13.5796 2.48755 13.3457 2.70681 13.3457 2.97728V3.467C13.3457 3.73747 13.5796 3.95673 13.8681 3.95673C14.1566 3.95673 14.3905 3.73747 14.3905 3.467V2.97728C14.3905 2.70681 14.1565 2.48755 13.8681 2.48755Z" fill="#188A06"/>
<path d="M12.824 1.50806H12.3017C12.0132 1.50806 11.7793 1.72731 11.7793 1.99778C11.7793 2.26825 12.0132 2.48751 12.3017 2.48751H12.824C13.1125 2.48751 13.3464 2.26825 13.3464 1.99778C13.3464 1.72731 13.1125 1.50806 12.824 1.50806Z" fill="#188A06"/>
<path d="M15.4354 1.50806H14.913C14.6245 1.50806 14.3906 1.72731 14.3906 1.99778C14.3906 2.26825 14.6245 2.48751 14.913 2.48751H15.4354C15.7239 2.48751 15.9578 2.26825 15.9578 1.99778C15.9578 1.72731 15.7238 1.50806 15.4354 1.50806Z" fill="#188A06"/>
</g>
<defs>
<clipPath id="clip0_1186_377">
<rect width="16" height="15" fill="white"/>
</clipPath>
</defs>
</svg>`;
  let volumeDiscountCelebrationTimer = null;

  function clearVolumeDiscountCelebration() {
    if (volumeDiscountCelebrationTimer) {
      clearTimeout(volumeDiscountCelebrationTimer);
      volumeDiscountCelebrationTimer = null;
    }

    document.querySelector('.vd-celebrate')?.remove();
    document.querySelector('.vd-celebrate__burst')?.remove();
  }

  function celebrateVolumeDiscountSelection(widget, tier, quantity) {
    if (!widget || !tier || !(Number(tier.discount) > 0)) return;
    if (widget.closest('#sticky-atc-wrapper, .add-to-cart__sticky')) return;

    const quantityForm = widget.closest('.product-form__quantity');
    const quantityRow = quantityForm?.querySelector('.price-per-item__container');
    if (!quantityForm || !quantityRow) return;

    const qty = Math.max(1, Number(quantity) || Number(tier.min) || 1);
    const pricing = calculateTierPricing(widget, tier);
    const saved = Math.max(0, pricing.savings);
    const discount = Number(tier.discount) || 0;
    const itemLabel = qty === 1 ? 'item' : 'items';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    clearVolumeDiscountCelebration();

    const node = document.createElement('div');
    node.className = 'vd-celebrate';
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.setAttribute('aria-atomic', 'true');

    const burst = document.createElement('div');
    burst.className = 'vd-celebrate__burst';
    burst.setAttribute('aria-hidden', 'true');

    if (!reduceMotion) {
      VOLUME_DISCOUNT_CONFETTI.forEach((piece) => {
        const bit = document.createElement('span');
        bit.className = `vd-celebrate__piece${piece.round ? ' is-round' : ''}`;
        bit.style.setProperty('--x', piece.x);
        bit.style.setProperty('--y', piece.y);
        bit.style.setProperty('--r', piece.r);
        bit.style.setProperty('--d', piece.d);
        bit.style.setProperty('--c', piece.c);
        burst.appendChild(bit);
      });
    }

    const text = document.createElement('p');
    text.className = 'vd-celebrate__text';
    const baseMessage = `Extra ${discount}% OFF unlocked at ${qty} ${itemLabel}`;
    const saveMessage =
      saved > 0 ? ` ( you save <span class="vd-celebrate__save">${formatMoney(saved, widget)}</span> )` : '';
    text.innerHTML = `<span class="vd-celebrate__icon">${VOLUME_DISCOUNT_CELEBRATION_ICON}</span> ${baseMessage}${saveMessage}`;

    node.appendChild(text);
    text.querySelector('.vd-celebrate__icon')?.appendChild(burst);
    quantityRow.prepend(node);

    volumeDiscountCelebrationTimer = setTimeout(() => {
      clearVolumeDiscountCelebration();
    }, VOLUME_DISCOUNT_CELEBRATION_MS);
  }

  function syncCelebrationFromQuantity(input) {
    if (syncingQuantity || !(input instanceof HTMLInputElement)) return;
    if (input.closest('#sticky-atc-wrapper, .add-to-cart__sticky, .sticky_atc_wrapper')) return;

    const quantity = Math.max(1, Number(input.value) || 1);
    const productInfo = input.closest('product-info');
    const widgets = productInfo
      ? productInfo.querySelectorAll(WIDGET_SELECTOR)
      : document.querySelectorAll(`product-info ${WIDGET_SELECTOR}`);
    let celebrated = false;

    widgets.forEach((widget) => {
      if (!widget.querySelector('[data-volume-discount-select]')) return;

      const tier = findTierByQuantity(quantity, getResolvedTiers(widget));
      if (!tier || !(Number(tier.discount) > 0)) return;

      celebrateVolumeDiscountSelection(widget, tier, quantity);
      celebrated = true;
    });

    if (!celebrated) clearVolumeDiscountCelebration();
  }
  function volumeDiscountStateText(expanded) {
    const strings = window.distaA11yStrings || {};
    return expanded ? strings.expanded || 'expanded' : strings.collapsed || 'collapsed';
  }

  function syncVolumeDiscountTrigger(trigger, announce) {
    if (!trigger) return;
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    const state = volumeDiscountStateText(expanded);
    const value = trigger.querySelector('.volume-discount__trigger-value');
    const name = (value && value.textContent ? value.textContent : 'Buy more Save more').replace(/\s+/g, ' ').trim();
    // aria-expanded already carries the state, so the name must not repeat it.
    trigger.setAttribute('aria-label', name);
    trigger.querySelectorAll('[data-volume-state]').forEach((stateEl) => stateEl.remove());
    if (announce && window.DistaA11y && window.DistaA11y.announce) {
      window.DistaA11y.announce(name + ', ' + state);
    }
  }

  function closeVolumeDiscountMenu(root, announce) {
    if (!root) return;

    const state = getWidgetState(root);
    const ui = state.ui || root.querySelector('[data-volume-discount-ui]');
    if (!ui) return;

    ui.classList.remove('is-open');

    const trigger = ui.querySelector('[data-volume-discount-trigger]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      syncVolumeDiscountTrigger(trigger, announce);
    }

    const overlay = state.overlay || ui.querySelector('.volume-discount__overlay');
    const menu = state.menu || ui.querySelector('.volume-discount__menu');

    overlay?.classList.remove('is-open');
    menu?.classList.remove('is-open');

    if (overlay && overlay.parentElement !== ui) {
      ui.insertBefore(overlay, trigger || ui.firstChild);
    }

    if (menu && menu.parentElement !== ui) {
      ui.appendChild(menu);
    }

    document.body.classList.remove('vd-no-scroll');
  }

  function closeAllVolumeDiscountMenus(exceptRoot = null) {
    document.querySelectorAll(WIDGET_SELECTOR).forEach((root) => {
      if (exceptRoot && root === exceptRoot) return;
      closeVolumeDiscountMenu(root);
    });
  }

  function toggleVolumeDiscountMenu(root) {
    if (!root) return;

    const state = getWidgetState(root);
    const ui = state.ui || root.querySelector('[data-volume-discount-ui]');
    const trigger = ui?.querySelector('[data-volume-discount-trigger]');
    if (!ui || !trigger) return;

    const isOpen = ui.classList.contains('is-open');
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    closeAllVolumeDiscountMenus(root);

    if (!isOpen) {
      const overlay = state.overlay || ui.querySelector('.volume-discount__overlay');
      const menu = state.menu || ui.querySelector('.volume-discount__menu');

      ui.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      syncVolumeDiscountTrigger(trigger, true);

      if (isMobile) {
        if (overlay && overlay.parentElement !== document.body) {
          document.body.appendChild(overlay);
        }

        if (menu && menu.parentElement !== document.body) {
          document.body.appendChild(menu);
        }

        overlay?.classList.add('is-open');
        menu?.classList.add('is-open');
        document.body.classList.add('vd-no-scroll');
      }
    } else {
      closeVolumeDiscountMenu(root, true);
    }
  }

  function ensureVolumeDiscountUi(root) {
    const container = root.querySelector('.volume-discount__select-label') || root;
    let ui = root.querySelector('[data-volume-discount-ui]');

    if (!ui) {
      ui = document.createElement('div');
      ui.className = 'volume-discount__custom-select';
      ui.dataset.volumeDiscountUi = 'true';

      const overlay = document.createElement('div');
      overlay.className = 'volume-discount__overlay';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'volume-discount__trigger';
      trigger.setAttribute('data-volume-discount-trigger', 'true');
      trigger.setAttribute('aria-expanded', 'false');

      const triggerValue = document.createElement('span');
      triggerValue.className = 'volume-discount__trigger-value';

      const arrow = document.createElement('span');
      arrow.className = 'volume-discount__arrow';
      arrow.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M5 7l5 5 5-5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"></path>
        </svg>
      `;

      const menu = document.createElement('div');
      menu.className = 'volume-discount__menu';
      menu.id = `volume-discount-menu-${root.dataset.sectionId || root.id || 'product'}`;
      trigger.setAttribute('aria-controls', menu.id);

      overlay.addEventListener('click', () => {
        closeVolumeDiscountMenu(root);
      });

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        toggleVolumeDiscountMenu(root);
      });

      ui.appendChild(overlay);
      ui.appendChild(trigger);
      trigger.appendChild(triggerValue);
      trigger.appendChild(arrow);
      ui.appendChild(menu);
      container.appendChild(ui);

      const state = getWidgetState(root);
      state.ui = ui;
      state.overlay = overlay;
      state.menu = menu;
    } else {
      const state = getWidgetState(root);
      state.ui = ui;
      state.overlay = ui.querySelector('.volume-discount__overlay') || state.overlay;
      state.menu = ui.querySelector('.volume-discount__menu') || state.menu;
    }

    return ui;
  }

  function buildVolumeDiscountCustomSelect(widget, select, activeTier, quantity, tiers) {
    if (!widget || !select) return;

    const ui = ensureVolumeDiscountUi(widget);
    const trigger = ui.querySelector('[data-volume-discount-trigger]');
    const triggerValue = ui.querySelector('.volume-discount__trigger-value');
    const menu = ui.querySelector('.volume-discount__menu');
    const placeholderOption = select.options[0];
    const placeholderText = placeholderOption
      ? String(placeholderOption.textContent || '').trim()
      : 'Buy More Save More';
    const selectedOption = select.options[select.selectedIndex] || placeholderOption;
    const selectedText =
      selectedOption && selectedOption.value ? String(selectedOption.textContent || '').trim() : placeholderText;

    select.classList.add('native-select-hidden');

    if (trigger) {
      trigger.classList.toggle('has-selection', Boolean(activeTier && Number(activeTier.discount) > 0));
    }

    if (triggerValue) {
      if (activeTier && Number(activeTier.discount) > 0) {
        const pricing = calculateTierPricing(widget, activeTier);
        triggerValue.innerHTML = `
          <span class="vd-selected">
            <span class="vd-selected-save">Savings ${formatMoney(pricing.savings, widget)}</span>
            <span class="vd-selected-off">${activeTier.discount}%</span>
          </span>
        `;
      } else {
        triggerValue.textContent = selectedText || placeholderText;
      }
    }

    if (trigger) syncVolumeDiscountTrigger(trigger);

    if (!menu) return;

    menu.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'vd-sheet-header';
    header.innerHTML = `
      <div class="vd-header-inner">
        <span class="vd-header-title">Select Quantity</span>
        <button type="button" class="vd-close-btn" aria-label="Close discounts">&times;</button>
      </div>
    `;
    menu.appendChild(header);

    const closeBtn = header.querySelector('.vd-close-btn');
    const closeSheet = () => closeVolumeDiscountMenu(widget, true);

    closeBtn?.addEventListener('click', closeSheet);

    tiers.forEach((tier, index) => {
      const option = select.options[index + 1];
      if (!option || !option.value) return;

      const pricing = calculateTierPricing(widget, tier);
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'volume-discount__menu-option';
      item.dataset.tierValue = option.value;

      if (option.disabled) {
        item.disabled = true;
        item.classList.add('disabled');
      }

      if (activeTier && option.value === activeTier.key) {
        item.classList.add('selected');
        item.setAttribute('aria-selected', 'true');
      }

      item.innerHTML = `
        <div class="vd-row">
          <div class="vd-left">
            <strong>${tier.min}${tier.max !== null ? `-${tier.max}` : '+'}</strong>
            <span class="vd-price">${formatMoney(pricing.finalPrice, widget)} /qty</span>
          </div>
          <div class="vd-right">
            <span class="vd-save">Save ${formatMoney(pricing.savings, widget)}</span>
            <span class="vd-off">${tier.discount}%</span>
          </div>
        </div>
      `;

            item.addEventListener('click', () => {
        if (option.disabled) return;

        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        closeVolumeDiscountMenu(widget, true);
        celebrateVolumeDiscountSelection(widget, tier, tier.min);
        widget.querySelector('[data-volume-discount-trigger]')?.focus();
      });

      menu.appendChild(item);
    });

    const existingCustomRow = menu.querySelector('.vd-custom-qty-row');
    if (existingCustomRow) existingCustomRow.remove();

    const customRow = document.createElement('div');
    customRow.className = 'vd-custom-qty-row';
    customRow.innerHTML = `
      <div class="vd-custom-row">
        <div class="vd-left">Custom Qty:</div>
        <div class="vd-right">
          <input type="number" min="1" class="vd-custom-input" />
          <button type="button" class="vd-custom-apply">Apply</button>
        </div>
      </div>
    `;
    menu.appendChild(customRow);

    const customInput = customRow.querySelector('.vd-custom-input');
    const customApply = customRow.querySelector('.vd-custom-apply');
    const maxQuantity = getMaxQuantity(widget);
    if (customInput) {
      customInput.value = quantity || getQuantity(widget) || 1;
      if (Number.isFinite(maxQuantity)) {
        customInput.max = maxQuantity;
      }
    }

    customInput?.addEventListener('input', () => {
      const value = Number(customInput.value);
      if (Number.isFinite(maxQuantity) && value > maxQuantity) {
        customInput.value = maxQuantity;
      }
    });

        customApply?.addEventListener('click', () => {
      const nextQty = Number(customInput?.value || quantity || 1);
      setQuantityInputs(nextQty);
      closeVolumeDiscountMenu(widget, true);

      const appliedTier = findTierByQuantity(nextQty, tiers);
      if (appliedTier) {
        celebrateVolumeDiscountSelection(widget, appliedTier, nextQty);
      }
    });

    customInput?.addEventListener('keypress', (event) => {
      if (event.key === 'Enter') {
        customApply?.click();
      }
    });
  }

  function updateWidgetMessage(widget, quantity, activeTier, tiers) {
    const message = widget.querySelector('[data-volume-discount-message]');
    if (!message) return;

    if (!tiers || tiers.length === 0) {
      message.textContent = '';
      message.hidden = true;
      widget.hidden = true;
      return;
    }

    widget.hidden = false;

    const nextTier = tiers.find((tier) => tier.min > quantity);
    let parts = [];

    if (!activeTier) {
      if (!nextTier) {
        message.textContent = '';
        message.hidden = true;
        return;
      }

      const remaining = Math.max(1, nextTier.min - quantity);
      parts = [
        { type: 'text', text: 'Add' },
        { type: 'off', text: String(remaining) },
        { type: 'text', text: 'more to unlock' },
        { type: 'off', text: `${nextTier.discount}% off` },
      ];
    } else if (!nextTier) {
      parts = [
        { type: 'text', text: 'You unlocked' },
        { type: 'off', text: `${activeTier.discount}% OFF` },
      ];
    } else {
      const remaining = Math.max(1, nextTier.min - quantity);
      parts = [
        { type: 'text', text: 'You unlocked' },
        { type: 'off', text: `${activeTier.discount}% OFF` },
        { type: 'text', text: 'Add' },
        { type: 'off', text: String(remaining) },
        { type: 'text', text: 'more to unlock' },
        { type: 'off', text: `${nextTier.discount}% off` },
      ];
    }

    message.innerHTML = parts.map((part) => `<span class="discount-display-${part.type}">${part.text}</span>`).join(' ');
    message.hidden = false;
  }

  function updateWidgetSelect(widget, activeTier, tiers, quantity) {
    const select = widget.querySelector('[data-volume-discount-select]');
    if (!select) return;

    const maxQty = getMaxQuantity(widget);
    const currentTierKey = activeTier ? activeTier.key : '';

    select.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Buy more Save more';
    select.appendChild(placeholder);

    tiers.forEach((tier) => {
      const option = document.createElement('option');
      option.value = tier.key;
      option.dataset.minQty = String(tier.min);
      option.dataset.maxQty = tier.max === null ? '' : String(tier.max);
      option.dataset.discountValue = String(tier.discount);
      option.textContent = `Buy ${tier.min}${tier.max !== null ? `-${tier.max}` : '+'} and Save ${tier.discount}%`;
      option.disabled = tier.min > maxQty;

      if (tier.key === currentTierKey) {
        option.selected = true;
      }

      select.appendChild(option);
    });

    select.value = currentTierKey || '';
    select.dataset.currentQuantity = String(quantity);
    buildVolumeDiscountCustomSelect(widget, select, activeTier, quantity, tiers);
  }

  function renderWidget(widget) {
    const state = getWidgetState(widget);
    if (!state.config) {
      widget.hidden = true;
      return;
    }

    const tiers = getResolvedTiers(widget);
    const quantity = getQuantity(widget);
    const activeTier = findTierByQuantity(quantity, tiers);
    const pricing = calculateTierPricing(widget, activeTier);

    updatePriceContainer(widget, pricing.finalPrice, pricing.comparePrice, pricing.displayDiscountPercent);
    updateWidgetMessage(widget, quantity, activeTier, tiers);
    updateWidgetSelect(widget, activeTier, tiers, quantity);
  }

  function renderAllWidgets() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(renderWidget);
  }

  function syncQuantityFromSelect(select) {
    const widget = select.closest(WIDGET_SELECTOR);
    if (!widget) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption || !selectedOption.value) {
      scheduleRender();
      return;
    }

    const tier = {
      key: selectedOption.value,
      min: Number(selectedOption.dataset.minQty || 0),
      max: selectedOption.dataset.maxQty ? Number(selectedOption.dataset.maxQty) : null,
      discount: Number(selectedOption.dataset.discountValue || 0),
    };

    if (!tier.min) {
      scheduleRender();
      return;
    }

    setQuantityInputs(tier.min);
    scheduleRender();
  }

  function onDocumentChange(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.matches('[data-volume-discount-select]')) {
      syncQuantityFromSelect(event.target);
      return;
    }

    if (event.target.matches(QUANTITY_SELECTOR) || event.target.closest(QUANTITY_SELECTOR)) {
      if (syncingQuantity) return;
      scheduleRender();
      if (event.target.matches('input')) {
        syncCelebrationFromQuantity(event.target);
      }
    }
  }

  function onDocumentInput(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.matches(QUANTITY_SELECTOR) || event.target.closest(QUANTITY_SELECTOR)) {
      if (syncingQuantity) return;
      scheduleRender();
    }
  }

  function onVolumeDiscountDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-volume-discount-trigger]');
    if (trigger) {
      if (trigger.dataset.keyboardToggle === 'true') {
        delete trigger.dataset.keyboardToggle;
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      const root = trigger.closest(WIDGET_SELECTOR);
      toggleVolumeDiscountMenu(root);
      return;
    }

    if (event.target.closest('[data-volume-discount-ui]')) return;

    closeAllVolumeDiscountMenus();
  }

  function onVolumeDiscountKeydown(event) {
    const trigger = event.target instanceof Element ? event.target.closest('[data-volume-discount-trigger]') : null;
    if (trigger && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      trigger.dataset.keyboardToggle = 'true';
      toggleVolumeDiscountMenu(trigger.closest(WIDGET_SELECTOR));
      return;
    }

    if (event.key !== 'Escape') return;

    const openMenu = document.querySelector('[data-volume-discount-ui].is-open');
    const openTrigger = openMenu?.querySelector('[data-volume-discount-trigger]');
    closeAllVolumeDiscountMenus();
    if (openTrigger) {
      syncVolumeDiscountTrigger(openTrigger, true);
      openTrigger.focus();
    }
  }

  function bindListeners() {
    if (listenersBound) return;

    document.addEventListener('change', onDocumentChange, true);
    document.addEventListener('input', onDocumentInput, true);
    document.addEventListener('click', onVolumeDiscountDocumentClick, true);
    document.addEventListener('keydown', onVolumeDiscountKeydown, true);
    document.addEventListener(
      'product-info:loaded',
      (event) => {
        const productInfo = event.target instanceof Element ? event.target : null;
        const variant = getSelectedVariantFromProductInfo(productInfo);
        const sectionId = productInfo?.dataset.section;

        if (variant) {
          syncWidgetsForVariant(variant, sectionId);
        }

        scheduleRender();
      },
      true
    );

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        const sectionId = event?.data?.sectionId;

        syncWidgetsForVariant(variant, sectionId);
        setTimeout(scheduleRender, 0);
      });
    }

    listenersBound = true;
  }

  function init() {
    bindListeners();
    renderAllWidgets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
